// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.

export type DocumentAssetStatus = 'PENDING' | 'READY' | 'FAILED';

export interface DocumentAssetReference {
  modelType: string;
  modelId: number;
  contentBlockId: string;
  contentState: 'draft' | 'published';
  fundOpportunityExternalId?: string;
  fundOpportunityName?: string;
}

export interface DocumentAsset {
  externalId: string;
  title: string;
  description?: string;
  createdByName?: string;
  createdAt?: string;
  updatedAt?: string;
  fileSizeBytes?: number;
  pageCount?: number;
  status: DocumentAssetStatus;
  ownerFundOpportunityId: number;
  ownerFundOpportunityExternalId?: string;
  originalFilename?: string;
  mimeType?: string;
  gcsWebPath?: string;
  errorMessage?: string;
  referenceCount?: number;
  references?: DocumentAssetReference[];
}

/**
 * Document library: admins upload a document once, and the same file can be
 * used to generate or insert content into any fund opportunity's content.
 * Upload is presigned (same shape as uploadAssetContent) so the file goes
 * straight to GCS — this service never holds the raw bytes in the browser's
 * fetch layer.
 */
export const adminBackendServiceDocumentLibraryMixin = {
  requestDocumentUpload(
    title: string,
    description: string,
    fundOpportunityId: string,
    fileName: string,
    contentType: string,
  ): Promise<DocumentAsset & { uploadUrl: string }> {
    return this.authApiMutateAuthed(
      'contentManagement/documents',
      'POST',
      { title, description, fundOpportunityId, fileName, contentType },
      (json: any) => json.data,
    );
  },

  completeDocumentUpload(externalId: string): Promise<DocumentAsset> {
    return this.authApiMutateAuthed(
      `contentManagement/documents/${externalId}/complete`,
      'POST',
      undefined,
      (json: any) => json.data,
    );
  },

  /** Full flow: request a signed URL, PUT the file to GCS, then confirm. */
  async uploadDocumentToLibrary(
    title: string,
    description: string,
    fundOpportunityId: string,
    file: File,
    onUploadProgress?: (progress: number) => void,
  ): Promise<DocumentAsset> {
    const contentType = file.type || 'application/octet-stream';
    const { externalId, uploadUrl } = await this.requestDocumentUpload(
      title,
      description,
      fundOpportunityId,
      file.name,
      contentType,
    );
    await this.putFileViaXhr(uploadUrl, file, contentType, onUploadProgress);
    return this.completeDocumentUpload(externalId);
  },

  listDocumentLibrary(
    params: { title?: string; fundOpportunityId?: string } = {},
  ): Promise<DocumentAsset[]> {
    const query = new URLSearchParams();
    if (params.title) query.set('title', params.title);
    if (params.fundOpportunityId) query.set('fundOpportunityId', params.fundOpportunityId);
    const queryString = query.toString();
    return this.authApiGetAuthed(
      `contentManagement/documents${queryString ? `?${queryString}` : ''}`,
      'Unable to load the document library',
      (json: any) => json.data ?? [],
    );
  },

  getDocumentAsset(externalId: string): Promise<DocumentAsset> {
    return this.authApiGetAuthed(
      `contentManagement/documents/${externalId}`,
      'Unable to load document',
      (json: any) => json.data,
    );
  },

  updateDocumentMetadata(
    externalId: string,
    fields: { title?: string; description?: string },
  ): Promise<DocumentAsset> {
    return this.authApiMutateAuthed(
      `contentManagement/documents/${externalId}`,
      'PATCH',
      fields,
      (json: any) => json.data,
    );
  },

  deleteDocumentAsset(externalId: string, force: boolean = false): Promise<{ deleted: boolean }> {
    return this.authApiMutateAuthed(
      `contentManagement/documents/${externalId}${force ? '?force=true' : ''}`,
      'DELETE',
      undefined,
      (json: any) => json.data,
    );
  },

  /**
   * /api path for a page raster. Pass to AssetImage — it resolves /api URLs
   * with the admin JWT (raw <img src> will 401).
   */
  getDocumentPageUrl(externalId: string, pageNumber: number): string {
    return this.apiURL(`contentManagement/documents/${externalId}/pages/${pageNumber}`);
  },

  importDocumentSelection(
    externalId: string,
    opts: {
      fromPage: number;
      toPage: number;
      box?: { x: number; y: number; width: number; height: number };
      modelType?: string;
      modelId?: string;
    },
  ): Promise<{
    blocks: any[];
    aiTier: 'fast' | 'thinking';
    /**
     * Set only for a single-page import that produced one ImageCarousel —
     * the retained raster + boxes, so the caller can offer the same
     * "Adjust crops" pass a screenshot paste would (`recropImageCards`).
     */
    cropSource?: { url: string; path: string; width: number; height: number } | null;
    cropBoxes?: Array<{ left: number; top: number; width: number; height: number }>;
    cropConfidence?: string;
  }> {
    return this.authApiMutateAuthed(
      `contentManagement/documents/${externalId}/import`,
      'POST',
      opts,
      (json: any) => json.data ?? json,
    );
  },

  cropDocumentRegion(
    externalId: string,
    opts: {
      page: number;
      box: { x: number; y: number; width: number; height: number };
      modelType: string;
      modelId: string;
      mode: 'image' | 'structure';
    },
  ): Promise<{ imageUrl?: string; type?: string; properties?: any }> {
    return this.authApiMutateAuthed(
      `contentManagement/documents/${externalId}/region`,
      'POST',
      opts,
      (json: any) => json.data ?? json,
    );
  },

  generateSectionFromDocument(opts: {
    documentAssetId: string;
    pageRange?: { from: number; to: number };
    blockType: string;
    adminPrompt: string;
    fundOpportunityId: string;
  }): Promise<{ type: string; properties: any }> {
    return this.authApiMutateAuthed(
      'contentManagement/block/generateFromDocument',
      'POST',
      opts,
      (json: any) => json.data,
    );
  },
};
